import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Coins,
  Zap,
  Crown,
  Sparkles,
  Brain,
  CreditCard,
  ArrowRight,
  Check,
  Star,
  RefreshCw,
  ExternalLink,
  Loader2,
  Clock,
  Gift,
  Stethoscope,
  Video,
  Calendar,
  TrendingUp,
  ArrowLeft,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Subscription plans for professionals
const PROFESSIONAL_PLANS = {
  gratuit: {
    name: 'Gratuit',
    credits_per_month: 0,
    features: [
      'Profil de base dans l&apos;annuaire',
      '5 rendez-vous par mois',
      '10 questions IA gratuites/mois',
    ],
    limitations: [
      'Commission sur téléconsultations',
      'Statistiques limitées',
    ],
    color: 'slate'
  },
  pro: {
    name: 'Pro',
    credits_per_month: 100,
    price_monthly: '4,000 FCFA',
    price_annual: '33,000 FCFA',
    features: [
      'Rendez-vous illimités',
      '100 questions IA/mois',
      'Statistiques complètes',
      'Badge "Pro Vérifié"',
      'Priorité dans les recherches',
    ],
    color: 'blue'
  },
  premium: {
    name: 'Premium',
    credits_per_month: 300,
    price_monthly: '12,000 FCFA',
    price_annual: '100,000 FCFA',
    features: [
      'Toutes les fonctionnalités Pro',
      'Questions IA illimitées',
      '0% commission sur téléconsultations',
      'Dossier médical avancé',
      'Support prioritaire',
      'Badge "Expert Premium"',
    ],
    color: 'purple'
  }
};

const ProfessionalCredits = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseReference, setPurchaseReference] = useState(null);
  const [pollingInterval, setPollingIntervalState] = useState(null);

  useEffect(() => {
    loadData();
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, packsRes, transRes] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getPacks(),
        creditsAPI.getTransactions()
      ]);
      setBalance(balanceRes.data);
      setPacks(packsRes.data.packs);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      console.error('Error loading credits data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const startPurchasePolling = useCallback((reference) => {
    const interval = setInterval(async () => {
      try {
        const response = await creditsAPI.checkPurchaseStatus(reference);
        if (response.data.status === 'confirmed') {
          clearInterval(interval);
          setPollingIntervalState(null);
          setPurchaseReference(null);
          toast.success(`${response.data.credits} crédits ajoutés à votre compte !`);
          loadData();
        }
      } catch (error) {
        console.error('Error checking purchase status:', error);
      }
    }, 5000);
    setPollingIntervalState(interval);
  }, []);

  const handlePurchase = async (packKey) => {
    setPurchasing(true);
    try {
      const response = await creditsAPI.purchase({ pack: packKey });
      const { reference, chariow_url } = response.data;
      
      setPurchaseReference(reference);
      toast.info('Redirection vers Chariow en cours...');
      
      // Try to open in new tab, fallback to redirect
      const newWindow = window.open(chariow_url, '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup blocked - redirect in same window
        toast.warning('Popup bloqué - redirection directe...');
        setTimeout(() => {
          window.location.href = chariow_url;
        }, 1000);
      } else {
        // New tab opened successfully, start polling
        startPurchasePolling(reference);
      }
      
    } catch (error) {
      console.error('Error initiating purchase:', error);
      toast.error('Erreur lors de la création de la commande');
    } finally {
      setPurchasing(false);
    }
  };

  const handleSubscribe = async (plan, billing = 'monthly') => {
    if (plan === 'gratuit') return;
    
    const planData = PROFESSIONAL_PLANS[plan];
    const creditsNeeded = billing === 'annual' 
      ? planData.credits_per_month * 10 
      : planData.credits_per_month;
    
    if ((balance?.balance || 0) < creditsNeeded) {
      toast.error(`Crédits insuffisants. Vous avez besoin de ${creditsNeeded} crédits.`);
      return;
    }

    try {
      const response = await creditsAPI.subscribe({ plan, billing });
      if (response.data.success) {
        toast.success(`Abonnement ${planData.name} activé !`);
        loadData();
      } else {
        toast.error(response.data.message || 'Erreur lors de l\'abonnement');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Erreur lors de l\'abonnement');
    }
  };

  const getPackIcon = (key) => {
    const icons = {
      decouverte: Zap,
      standard: Star,
      pro: Crown,
      elite: Sparkles
    };
    return icons[key] || Coins;
  };

  const getPackColor = (key) => {
    const colors = {
      decouverte: 'from-blue-500 to-cyan-500',
      standard: 'from-purple-500 to-pink-500',
      pro: 'from-amber-500 to-orange-500',
      elite: 'from-emerald-500 to-teal-500'
    };
    return colors[key] || 'from-gray-500 to-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  const currentPlan = balance?.subscription || 'gratuit';
  const aiUsed = balance?.ai_questions_used_this_month || 0;
  const aiFree = balance?.ai_questions_free || 10;
  const aiRemaining = balance?.ai_questions_remaining_free;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="professional-credits-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/pro/dashboard')}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour au tableau de bord
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-[#E53935]" />
              Crédits & Abonnement
            </h1>
            <p className="text-slate-500 mt-1">Gérez vos crédits et boostez votre visibilité</p>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-[#E53935] to-[#C62828] text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-white/70 text-sm mb-1">Solde actuel</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold">{balance?.balance || 0}</p>
                  <span className="text-white/70">crédits</span>
                </div>
              </div>
              
              {/* Current Plan */}
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/70 text-sm mb-1">Plan actuel</p>
                <div className="flex items-center gap-2">
                  {currentPlan === 'premium' ? <Sparkles className="w-5 h-5" /> :
                   currentPlan === 'pro' ? <BadgeCheck className="w-5 h-5" /> :
                   <Gift className="w-5 h-5" />}
                  <span className="text-xl font-bold capitalize">
                    {PROFESSIONAL_PLANS[currentPlan]?.name || 'Gratuit'}
                  </span>
                </div>
                {balance?.subscription_expires && currentPlan !== 'gratuit' && (
                  <p className="text-xs text-white/60 mt-1">
                    Expire le {format(new Date(balance.subscription_expires), 'dd MMM yyyy', { locale: fr })}
                  </p>
                )}
              </div>
            </div>

            {/* AI Questions Progress */}
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="flex items-center gap-2 text-white/80">
                  <Brain className="w-4 h-4" />
                  Questions IA ce mois
                </span>
                <span className="font-medium">
                  {aiRemaining === -1 
                    ? 'Illimitées' 
                    : `${aiRemaining} / ${aiFree} restantes`
                  }
                </span>
              </div>
              {aiFree !== -1 && aiRemaining !== -1 && (
                <Progress 
                  value={(aiRemaining / aiFree) * 100} 
                  className="h-2 bg-white/20"
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Polling indicator */}
      {purchaseReference && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="p-4 flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900">Vérification du paiement en cours...</p>
              <p className="text-xs text-purple-700">Référence: {purchaseReference}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="plans">Abonnements</TabsTrigger>
          <TabsTrigger value="packs">Acheter</TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        {/* Subscription Plans */}
        <TabsContent value="plans" className="mt-6">
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(PROFESSIONAL_PLANS).map(([key, plan]) => (
              <Card 
                key={key} 
                className={`relative ${currentPlan === key ? 'ring-2 ring-emerald-500' : ''} ${
                  key === 'premium' ? 'border-purple-200' : 
                  key === 'pro' ? 'border-blue-200' : ''
                }`}
              >
                {currentPlan === key && (
                  <div className="absolute -top-3 left-4 bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Actuel
                  </div>
                )}
                {key === 'pro' && currentPlan === 'gratuit' && (
                  <div className="absolute -top-3 right-4 bg-blue-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                    Recommandé
                  </div>
                )}
                <CardHeader className="pb-2">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                    key === 'premium' ? 'bg-purple-100' :
                    key === 'pro' ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {key === 'premium' ? <Sparkles className="w-6 h-6 text-purple-600" /> :
                     key === 'pro' ? <BadgeCheck className="w-6 h-6 text-blue-600" /> :
                     <Gift className="w-6 h-6 text-slate-600" />}
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>
                    {plan.credits_per_month > 0 
                      ? `${plan.credits_per_month} crédits/mois`
                      : 'Gratuit à vie'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-2">
                  {plan.price_monthly && (
                    <p className="text-2xl font-bold text-slate-900 mb-4">
                      {plan.price_monthly}
                      <span className="text-sm font-normal text-slate-500">/mois</span>
                    </p>
                  )}
                  <ul className="space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.limitations?.map((limitation, i) => (
                      <li key={`lim-${i}`} className="flex items-start gap-2 text-sm text-slate-400">
                        <span className="w-4 h-4 flex items-center justify-center">✗</span>
                        <span>{limitation}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                {key !== 'gratuit' && currentPlan !== key && (
                  <CardFooter className="flex-col gap-2 pt-4">
                    <Button 
                      className={`w-full ${
                        key === 'premium' ? 'bg-purple-600 hover:bg-purple-700' :
                        'bg-blue-600 hover:bg-blue-700'
                      }`}
                      onClick={() => handleSubscribe(key, 'monthly')}
                    >
                      S&apos;abonner ({plan.credits_per_month} crédits)
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full text-xs"
                      onClick={() => handleSubscribe(key, 'annual')}
                    >
                      Annuel ({plan.credits_per_month * 10} crédits - 2 mois offerts)
                    </Button>
                  </CardFooter>
                )}
              </Card>
            ))}
          </div>

          {/* Teleconsultation Info */}
          <Card className="mt-6 border-green-200 bg-green-50">
            <CardContent className="p-4">
              <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Video className="w-5 h-5" />
                Téléconsultations
              </h4>
              <p className="text-sm text-green-700">
                Avec l&apos;abonnement <strong>Premium</strong>, vous ne payez <strong>aucune commission</strong> sur vos téléconsultations. 
                Les patients paient directement via Chariow et vous recevez 100% du montant.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Packs */}
        <TabsContent value="packs" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {packs.map((pack) => {
              const Icon = getPackIcon(pack.key);
              const gradient = getPackColor(pack.key);
              
              return (
                <Card 
                  key={pack.key} 
                  className={`relative overflow-hidden hover:shadow-lg transition-all ${pack.popular ? 'ring-2 ring-purple-500' : ''}`}
                >
                  {pack.popular && (
                    <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Populaire
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{pack.name}</CardTitle>
                    <CardDescription className="text-xs">{pack.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-3xl font-bold text-slate-900">{pack.credits}</span>
                      <span className="text-slate-500 text-sm">crédits</span>
                    </div>
                    <p className="text-lg font-semibold text-[#E53935]">
                      {pack.price_fcfa.toLocaleString()} FCFA
                    </p>
                    <p className="text-xs text-slate-500">
                      {pack.price_per_credit} FCFA/crédit
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      onClick={() => handlePurchase(pack.key)}
                      disabled={purchasing}
                      className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90`}
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Acheter <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* AI Credit Info */}
          <Card className="mt-6 border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Assistant IA
              </h4>
              <p className="text-sm text-purple-700">
                Après vos {aiFree} questions IA gratuites mensuelles, chaque question coûte <strong>1 crédit</strong>. 
                L&apos;IA vous aide à rédiger des ordonnances, analyser des symptômes et consulter la littérature médicale.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transaction History */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Aucune transaction</p>
                  <p className="text-sm text-slate-400">Vos achats et abonnements apparaîtront ici</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                          }`}>
                            {tx.type === 'purchase' ? (
                              <CreditCard className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            ) : tx.type === 'ai_usage' ? (
                              <Brain className="w-5 h-5 text-red-600" />
                            ) : tx.type === 'subscription' ? (
                              <Crown className="w-5 h-5 text-red-600" />
                            ) : (
                              <Coins className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(tx.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfessionalCredits;
